import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as reporter from 'lib/reporter';
import { setPlayerState } from 'state/player/actions';
import { PlaybackState } from 'state/player/reducer';
import { getPlayerState } from 'state/player/selectors';

import {
  buildToggleVisibilityOrchestrator,
  toggleVisibilityOrchestrator,
} from '../ToggleVisibilityOrchestrator';

import {
  buildMediaPlayerOrchestrator,
  mediaPlayerOrchestrator,
} from './MediaPlayerOrchestrator';

export const useInitializeTimedOrchestrators = (): boolean => {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);
  const playerState = useSelector(getPlayerState);

  useEffect(() => {
    const lastOrchestratorWasLocked =
      mediaPlayerOrchestrator?.getPlayLock() ||
      toggleVisibilityOrchestrator?.getPlayLock();
    const playerStateInitialized = playerState !== PlaybackState.UNINITIALIZED;
    const setPlayLock = lastOrchestratorWasLocked && playerStateInitialized;

    buildMediaPlayerOrchestrator();
    buildToggleVisibilityOrchestrator();

    if (setPlayLock) {
      mediaPlayerOrchestrator.setPlayLock();
      toggleVisibilityOrchestrator.setPlayLock();
    }

    mediaPlayerOrchestrator.setOnPlayFailHandler(error => {
      reporter.log(`${error}`, error, { silent: true });
      toggleVisibilityOrchestrator.pause();
      dispatch(setPlayerState(PlaybackState.ERROR));
    });
    mediaPlayerOrchestrator.setOnPlaySuccessHandler(() => {
      dispatch(setPlayerState(PlaybackState.PLAYING));
    });
    mediaPlayerOrchestrator.setOnEndHandler(() => {
      dispatch(setPlayerState(PlaybackState.FINISHED));
    });
    mediaPlayerOrchestrator.setOnPauseHandler(() => {
      dispatch(setPlayerState(PlaybackState.PAUSED));
    });
    setIsReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // WARNING: Bug if playerState is included: https://github.com/sleepio/react-native-app/pull/1748

  // Make sure we teardown the orchestrator on unmount. If we don't, we'll have memory leaks not to mention
  // unexpected null/undefined'd player refs potentially hanging around which we may attempt to call and
  // crash the app.
  useEffect(() => {
    return () => {
      mediaPlayerOrchestrator.teardown();
      toggleVisibilityOrchestrator.teardown();
      setIsReady(false);
      dispatch(setPlayerState(PlaybackState.UNINITIALIZED));
    };
  }, [dispatch]);
  return isReady;
};
